import React from 'react';

/**
 * Global error boundary. Any render-time crash below this component (e.g. a
 * bad prop, a bad timezone, a null-deref) is caught and replaced with a
 * branded fallback UI. Without this, React 19 unmounts the whole tree on a
 * single thrown error, leaving `#root` blank.
 *
 * The boundary MUST wrap the router (not sit below individual pages) so a
 * single crashing route can't blank the whole app.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to console so PostHog / rrweb / Sentry etc. pick it up.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render crash:', error, info?.componentStack);
    try {
      if (window.posthog?.capture) {
        window.posthog.capture('$exception', {
          $exception_message: String(error?.message || error),
          $exception_stack_trace_raw: info?.componentStack || '',
          $exception_source: 'react_error_boundary',
        });
      }
    } catch (_) { /* noop */ }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen w-full flex items-center justify-center px-6 py-16"
        style={{
          background:
            'radial-gradient(1200px 600px at 15% 10%, rgba(191,167,124,0.18), transparent 60%),' +
            ' radial-gradient(900px 500px at 85% 90%, rgba(178,60,60,0.14), transparent 60%),' +
            ' linear-gradient(135deg, #1a1512 0%, #241a15 40%, #2d1e1a 100%)',
          color: '#f5efe5',
        }}
        data-testid="global-error-boundary"
      >
        <div className="max-w-lg w-full text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[#bfa77c] mb-4">Dostination</div>
          <h1
            className="font-serif text-3xl md:text-4xl font-semibold mb-4"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Something went wrong on this page.
          </h1>
          <p className="text-sm md:text-base text-[#d9cfbf]/80 mb-8 leading-relaxed">
            The page hit an unexpected error. Our team has been notified.
            Try reloading, or head back to browse other experiences.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="px-6 py-2.5 rounded-md bg-[#bfa77c] text-[#1a1512] font-semibold hover:bg-[#d3bd93] transition-colors"
              data-testid="error-boundary-reload-btn"
            >
              Reload page
            </button>
            <a
              href="/events"
              className="px-6 py-2.5 rounded-md border border-[#bfa77c]/50 text-[#f5efe5] hover:bg-[#bfa77c]/10 transition-colors"
              data-testid="error-boundary-events-link"
            >
              Back to events
            </a>
          </div>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre className="mt-8 text-[10px] text-left text-[#e8b4a4] bg-black/30 rounded p-3 overflow-auto max-h-40">
              {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
